// Local-mode mocks for the Applications product: AuthNull as the identity
// provider for a tenant's own apps. Every POST /api/v1/idp/* call (see
// src/service/idpService.js and authnull-service internal/idp/admin.go), plus
// the directory calls the screens read: /ad/GetAllDomains (with an Entra
// directory), /ad/GetAllAdUsers and /ad/GetAllGroupsByDomain.
//
// Stateful until reload. Seeded like dev: "IdP Test App" (single-page OIDC)
// and "AMSI ERP (MFA API test)", both assigned to one Entra user, and one Entra
// sign-in source on directory 27 "test1".
//
// Substring match, first wins — so the longer of two overlapping paths comes
// first ("/apps/saveassignments" before "/apps/save").
//
// The mock adapter always answers 200, so the server's 400s (a duplicate SAML
// entity ID, a bad redirect URI…) can't be shown here: save keeps the data
// consistent instead (a duplicate entity ID is simply stored), and the form
// checks the same rules client-side first.

import { MOCK_AD_DOMAINS } from "./sshProxyMocks";

const ISSUER = "http://localhost:3000/api/v1/idp";
// As internal/idp/idp.go EndpointsFor.
const ENDPOINTS = {
  issuer: ISSUER,
  discoveryUrl: `${ISSUER}/.well-known/openid-configuration`,
  jwksUrl: `${ISSUER}/oauth2/jwks`,
  authorizationEndpoint: `${ISSUER}/oauth2/authorize`,
  tokenEndpoint: `${ISSUER}/oauth2/token`,
  userinfoEndpoint: `${ISSUER}/oauth2/userinfo`,
  endSessionEndpoint: `${ISSUER}/oauth2/logout`,
  revocationEndpoint: `${ISSUER}/oauth2/revoke`,
  upstreamRedirectUri: `${ISSUER}/login/upstream/callback`,
  samlMetadataUrl: `${ISSUER}/saml/metadata`,
  samlEntityId: `${ISSUER}/saml/metadata`,
  samlSsoUrl: `${ISSUER}/saml/sso`,
};

// A throwaway self-signed certificate body, one line (the fetch mock
// JSON-encodes the answer, which would turn line breaks into "\n").
const MOCK_CERT =
  "MIIBszCCAVmgAwIBAgIUQmFzZTY0TW9ja0NlcnRpZmljYXRlMAoGCCqGSM49BAMCMC8xLTArBgNVBAMMJGF1dGhudWxsLWxvY2FsLW1vY2staWRwLXNpZ25pbmcta2V5MB4XDTI2MDEwMTAwMDAwMFoXDTM2MDEwMTAwMDAwMFowLzEtMCsGA1UEAwwkYXV0aG51bGwtbG9jYWwtbW9jay1pZHAtc2lnbmluZy1rZXkwWTATBgcqhkjOPQIBBggqhkjOPQMBBwNCAAQ=";

const ENTRA_DOMAIN = {
  id: 27,
  directoryName: "test1",
  domainName: "satyamgupta1807outlook.onmicrosoft.com",
  identity_source: "EntraID",
  integration_type: "Agentless",
  status: "Active",
  userCount: 3,
  groupCount: 2,
};

const DIRECTORY_USERS = [
  { id: 18578, emailId: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com", user_principal_name: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com", display_name: "Satyam (Entra)", username: "satyam.entra", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "NormalUser" },
  { id: 18579, emailId: "priya.k@satyamgupta1807outlook.onmicrosoft.com", user_principal_name: "priya.k@satyamgupta1807outlook.onmicrosoft.com", display_name: "Priya K", username: "priya.k", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "NormalUser" },
  { id: 18580, emailId: "ops.bot@satyamgupta1807outlook.onmicrosoft.com", user_principal_name: "ops.bot@satyamgupta1807outlook.onmicrosoft.com", display_name: "Ops Bot", username: "ops.bot", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "ServiceAccount" },
];

const DIRECTORY_GROUPS = [
  { id: 501, groupName: "ERP-Users", adId: 27 },
  { id: 502, groupName: "VPN-Users", adId: 27 },
];

const now = () => new Date().toISOString();
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
const newSecret = () => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  let s = "ancs_";
  for (let i = 0; i < 43; i += 1) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
};
// What the console may show of a secret (apps.go secretHint): "ancs_" and four
// more characters, with when it was made.
const secretFields = (secret) => ({ secretHint: secret.slice(0, 9), secretCreatedAt: now() });

const apps = [
  {
    id: "82fb7e5f-1c4d-4f7a-9d3e-5b2a6c8e0f11",
    tenantId: 1,
    name: "IdP Test App",
    protocol: "oidc",
    enabled: true,
    logoUrl: "",
    mfaMode: "always",
    oidc: {
      clientId: "c2fb8383-6a1e-4b0f-8d2c-3e4f5a6b7c8d",
      tokenEndpointAuthMethod: "none",
      redirectUris: ["http://localhost:8765/callback"],
      postLogoutRedirectUris: [],
      allowedScopes: ["openid", "profile", "email"],
      requirePkce: true,
      hasSecret: false,
    },
    createdAt: "2026-10-20T09:12:00Z",
    updatedAt: "2026-10-20T09:12:00Z",
  },
  {
    id: "4a7c2e90-8b3d-4e1f-a6c5-0d9e8f7a6b5c",
    tenantId: 1,
    name: "AMSI ERP (MFA API test)",
    protocol: "mfa_api",
    enabled: true,
    logoUrl: "",
    mfaMode: "always",
    oidc: {
      clientId: "9e1d4c7b-2a5f-4d8e-b3c6-7f0a1e2d3c4b",
      tokenEndpointAuthMethod: "client_secret_basic",
      redirectUris: [],
      postLogoutRedirectUris: [],
      allowedScopes: ["mfa"],
      requirePkce: false,
      hasSecret: true,
      secretHint: "ancs_Wad4",
      secretCreatedAt: "2026-10-21T14:40:00Z",
    },
    createdAt: "2026-10-21T14:40:00Z",
    updatedAt: "2026-10-21T14:40:00Z",
  },
];

const SATYAM = { type: "user", principal: "18578", label: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com" };
// appId → [{ type, principal }]
const assignments = {
  [apps[0].id]: [{ ...SATYAM }],
  [apps[1].id]: [{ ...SATYAM }],
};

const loginSources = [
  {
    id: "5d2e8f1a-3b4c-4d5e-9f6a-7b8c9d0e1f2a",
    tenantId: 1,
    kind: "entra",
    identitySourceId: 27,
    identitySourceName: "test1",
    priority: 100,
    domainSuffixes: [],
    enabled: true,
    createdAt: "2026-10-20T09:00:00Z",
    updatedAt: "2026-10-20T09:00:00Z",
  },
];

const KNOWN_SCOPES = ["openid", "profile", "email", "groups"];
const scopesOf = (list) => {
  const want = new Set(["openid", ...(list && list.length ? list : ["profile", "email"])]);
  return KNOWN_SCOPES.filter((s) => want.has(s));
};
const cleanList = (list) => [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))];

// Entity ID and HTTP-POST ACS URLs out of pasted SP metadata, as the server does.
const fromMetadata = (xml = "") => {
  const entity = (xml.match(/entityID="([^"]+)"/) || [])[1] || "";
  const acs = [];
  const re = /<(?:\w+:)?AssertionConsumerService\b([^>]*)>/g;
  let m;
  while ((m = re.exec(xml))) {
    const attrs = m[1];
    if (/Binding="[^"]*HTTP-POST"/.test(attrs)) {
      const loc = (attrs.match(/Location="([^"]+)"/) || [])[1];
      if (loc) acs.push(loc);
    }
  }
  return { entity, acs };
};

const labelOf = (a) => {
  if (a.type !== "user") return undefined;
  const u = DIRECTORY_USERS.find((x) => String(x.id) === String(a.principal));
  return u ? u.user_principal_name : "";
};

const assignmentsOf = (id) =>
  (assignments[id] || [])
    .map((a) => ({ type: a.type, principal: a.principal, ...(a.type === "user" ? { label: labelOf(a) } : {}) }))
    .sort((x, y) => x.type.localeCompare(y.type) || x.principal.toLowerCase().localeCompare(y.principal.toLowerCase()));

const saveAppMock = (body = {}) => {
  const protocol = body.protocol || "oidc";
  const existing = body.id ? apps.find((a) => a.id === body.id) : null;
  const app = existing
    ? { ...existing }
    : { id: uuid(), tenantId: Number(body.tenantId) || 1, protocol, mfaMode: "always", createdAt: now() };
  Object.assign(app, {
    name: String(body.name || "").trim(),
    logoUrl: String(body.logoUrl || "").trim(),
    enabled: !body.disabled,
    updatedAt: now(),
  });
  let clientSecret;
  if (protocol === "saml") {
    const s = body.saml || {};
    const meta = s.metadata ? fromMetadata(s.metadata) : { entity: "", acs: [] };
    const acs = cleanList(s.acsUrls);
    app.saml = {
      entityId: String(s.entityId || "").trim() || meta.entity,
      acsUrls: acs.length ? acs : meta.acs,
      nameIdFormat: s.nameIdFormat || "emailAddress",
      nameIdSource: s.nameIdSource || "email",
      attributes: s.attributes
        ? Object.fromEntries(Object.entries(s.attributes).map(([k, v]) => [k, String(v || "").trim()]).filter(([, v]) => v))
        : { email: "email", name: "name", username: "username", groups: "groups" },
    };
    delete app.oidc;
  } else if (protocol === "mfa_api") {
    const prev = existing?.oidc;
    if (!existing) clientSecret = newSecret();
    app.oidc = {
      clientId: prev?.clientId || uuid(),
      tokenEndpointAuthMethod: body.oidc?.tokenEndpointAuthMethod || "client_secret_basic",
      redirectUris: [],
      postLogoutRedirectUris: [],
      allowedScopes: ["mfa"],
      requirePkce: false,
      hasSecret: true,
      ...(clientSecret
        ? secretFields(clientSecret)
        : { secretHint: prev?.secretHint || "", secretCreatedAt: prev?.secretCreatedAt || now() }),
    };
  } else {
    const o = body.oidc || {};
    const method = o.tokenEndpointAuthMethod || "client_secret_basic";
    const confidential = method !== "none";
    const hadSecret = !!existing?.oidc?.hasSecret;
    // A secret is made on create, and when a public client becomes confidential.
    if (confidential && !hadSecret) clientSecret = newSecret();
    let secretInfo = {};
    if (clientSecret) secretInfo = secretFields(clientSecret);
    else if (confidential) secretInfo = { secretHint: existing.oidc.secretHint || "", secretCreatedAt: existing.oidc.secretCreatedAt || now() };
    app.oidc = {
      clientId: existing?.oidc?.clientId || uuid(),
      tokenEndpointAuthMethod: method,
      redirectUris: cleanList(o.redirectUris),
      postLogoutRedirectUris: cleanList(o.postLogoutRedirectUris),
      allowedScopes: scopesOf(o.allowedScopes),
      requirePkce: o.requirePkce === undefined ? true : !!o.requirePkce,
      hasSecret: confidential,
      ...secretInfo,
    };
  }
  if (existing) apps[apps.indexOf(existing)] = app;
  else apps.push(app);
  return clientSecret ? { app, clientSecret } : { app };
};

const saveSourceMock = (body = {}) => {
  const existing = body.id ? loginSources.find((s) => s.id === body.id) : null;
  const dir = Number(body.identitySourceId) === ENTRA_DOMAIN.id ? ENTRA_DOMAIN : null;
  const src = {
    ...(existing || { id: uuid(), createdAt: now(), priority: 100 }),
    tenantId: Number(body.tenantId) || 1,
    kind: body.kind || "entra",
    identitySourceId: Number(body.identitySourceId) || 0,
    identitySourceName: dir ? dir.directoryName : "",
    domainSuffixes: cleanList((body.domainSuffixes || []).map((d) => String(d).toLowerCase().replace(/^@/, ""))),
    enabled: !body.disabled,
    updatedAt: now(),
  };
  if (existing) loginSources[loginSources.indexOf(existing)] = src;
  else loginSources.push(src);
  return { loginSource: src };
};

const removeWhere = (list, pred) => {
  const i = list.findIndex(pred);
  if (i >= 0) list.splice(i, 1);
};

export const IDP_MOCKS = [
  { pattern: "/idp/apps/list", respond: () => ({ apps: apps.map((a) => ({ ...a })) }) },
  // Before "/idp/apps/save", which is a substring of it.
  {
    pattern: "/idp/apps/saveassignments",
    respond: (body = {}) => {
      assignments[body.id] = (body.assignments || []).slice(0, 500).map((a) => ({ type: a.type, principal: String(a.principal) }));
      return { assignments: assignmentsOf(body.id) };
    },
  },
  { pattern: "/idp/apps/save", respond: (body) => saveAppMock(body || {}) },
  {
    pattern: "/idp/apps/delete",
    respond: (body = {}) => {
      removeWhere(apps, (a) => a.id === body.id);
      delete assignments[body.id];
      return { status: "deleted" };
    },
  },
  {
    pattern: "/idp/apps/rotatesecret",
    respond: (body = {}) => {
      // Like the server, this does not change the app's updatedAt.
      const app = apps.find((a) => a.id === body.id);
      const clientSecret = newSecret();
      if (app?.oidc) app.oidc = { ...app.oidc, hasSecret: true, ...secretFields(clientSecret) };
      return { clientSecret };
    },
  },
  { pattern: "/idp/apps/assignments", respond: (body = {}) => ({ assignments: assignmentsOf(body.id) }) },
  { pattern: "/idp/loginsources/list", respond: () => ({ loginSources: loginSources.map((s) => ({ ...s })) }) },
  { pattern: "/idp/loginsources/save", respond: (body) => saveSourceMock(body || {}) },
  {
    pattern: "/idp/loginsources/delete",
    respond: (body = {}) => {
      removeWhere(loginSources, (s) => s.id === body.id);
      return { status: "deleted" };
    },
  },
  { pattern: "/idp/settings", respond: () => ({ endpoints: { ...ENDPOINTS } }) },
  // Public: fetched by "Download certificate". The fetch mock JSON-encodes
  // this string; the screen reads the certificate out of the text either way.
  {
    pattern: "/idp/saml/metadata",
    respond: () =>
      `<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata" xmlns:ds="http://www.w3.org/2000/09/xmldsig#" entityID="${ENDPOINTS.samlEntityId}"><md:IDPSSODescriptor><md:KeyDescriptor use="signing"><ds:KeyInfo><ds:X509Data><ds:X509Certificate>${MOCK_CERT}</ds:X509Certificate></ds:X509Data></ds:KeyInfo></md:KeyDescriptor></md:IDPSSODescriptor></md:EntityDescriptor>`,
  },

  // Directory calls. Ahead of sshProxyMocks' static "/ad/getalldomains":
  // the Entra directory is listed only when Entra directories are asked for,
  // so the other screens keep the list they had.
  {
    pattern: "/ad/getalldomains",
    respond: (body = {}) => {
      const source = body?.filters?.identitySource;
      const domains = source === "EntraID" ? [ENTRA_DOMAIN] : source ? [] : MOCK_AD_DOMAINS;
      return { code: 200, status: "success", domains, totalCount: domains.length };
    },
  },
  {
    pattern: "/ad/getalladusers",
    respond: (body = {}) => {
      const f = body?.filters || {};
      const q = String(f.emailId || "").toLowerCase();
      const dir = String(f.directoryName || "").toLowerCase();
      const users = DIRECTORY_USERS.filter(
        (u) => (!q || u.emailId.toLowerCase().includes(q)) && (!dir || u.activeDirectoryName.toLowerCase().startsWith(dir)),
      );
      return { code: 200, status: "Success", users, count: users.length };
    },
  },
  {
    pattern: "/ad/getallgroupsbydomain",
    respond: (body = {}) => {
      const q = String(body?.filter || "").toLowerCase();
      const groups = DIRECTORY_GROUPS.filter(
        (g) => (!body?.adId || g.adId === Number(body.adId)) && g.groupName.toLowerCase().includes(q),
      ).map(({ id, groupName }) => ({ id, groupName }));
      return { code: 200, status: "Success", groups, count: groups.length };
    },
  },
];
